import { apiClient } from "./client";
import type { ValueKind } from "./types";

// ── SQDC Board Types (D-O86, O-R17, O-R18, O-R22, O-R34, Q-O9) ───────────────────────────────────

export interface SqdcShopOption {
  scope: string;
  code: string;
  label: string;
}

export interface SqdcSecondaryFact {
  key: string;
  label: string;
  formattedValue: string;
  valueKind: ValueKind;
}

export interface SqdcPeriodCell {
  period: string;
  label: string;
  from: string;
  to: string;
  primaryValue: number | null;
  formattedPrimaryValue: string;
  primaryUnit: string;
  previousValue: number | null;
  formattedPreviousValue: string;
  delta: number | null;
  formattedDelta: string;
  targetState: "met" | "near" | "missed" | "noTarget" | "noData" | string;
  statusLabel: string;
  formattedTarget: string;
  facts: SqdcSecondaryFact[];
}

export interface SqdcTile {
  letter: "safety" | "quality" | "delivery" | "capacity" | string;
  initial: string;
  title: string;
  subtitle: string;
  plainDefinition: string;
  formulaText: string;
  sourceRule: string;
  valueKind: ValueKind;
  selectedPeriod: string;
  current: SqdcPeriodCell;
  periods: SqdcPeriodCell[];
  sparklineValues: (number | null)[];
  insightTopic: string;
  insightSubjectKey: string;
  linkedKpiId: string;
  canEditTarget: boolean;
}

export interface SqdcSafetyDay {
  date: string;
  state: "safe" | "reported" | "future" | "beforeFloor" | string;
  reports: number;
}

export interface SqdcSafetyCalendar {
  year: number;
  daysSinceLastRecordable: number | null;
  lastSafetyReportDate: string | null;
  safeDaysCount: number;
  reportedDaysCount: number;
  days: SqdcSafetyDay[];
}

export interface SqdcBoard {
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean;
  dataSource: string;
  plantDate: string;
  dataFloorDate: string;
  scope: string;
  scopeLabel: string;
  selectedPeriod: string;
  selectedPeriodLabel: string;
  canSetTargets: boolean;
  availableScopes: SqdcShopOption[];
  tiles: SqdcTile[];
  safetyCalendar: SqdcSafetyCalendar;
  notes: string[];
}

export interface SqdcTarget {
  letter: string;
  title: string;
  primarySettingKey: string;
  targetValue: number;
  amberBandSettingKey: string | null;
  amberBandValue: number | null;
  unit: string;
  direction: string;
  ruleId: string;
  description: string;
  valueKind: ValueKind;
}

export interface UpdateSqdcTargetPayload {
  targetValue: number;
  amberBandValue?: number | null;
  reason: string;
}

export interface SqdcDrillRow {
  key: string;
  title: string;
  category: string;
  date: string | null;
  workOrderKey: string | null;
  workcenterCode: string | null;
  formattedMetric: string;
  statusLabel: string;
  tone: string;
  linkPath: string | null;
  valueKind: ValueKind;
}

export interface SqdcDrill {
  letter: string;
  title: string;
  scope: string;
  period: string;
  from: string;
  to: string;
  totalCount: number;
  rows: SqdcDrillRow[];
}

// ── Wall Display Types (D-O86, O-R24, O-R34, D-O64) ──────────────────────────────────────────────

export interface WallDisplaySummaryItem {
  key: string;
  title: string;
  boardKind: string;
  shopCode: string | null;
  refreshSeconds: number;
  panels: string[];
}

export interface WallTickerItem {
  signalKey: string;
  severity: "critical" | "attention" | "info" | "good-news" | string;
  severityLabel: string;
  text: string;
  valueKind: ValueKind;
}

export interface WallTicker {
  fingerprint: string;
  speedDefault: string;
  totalSignals: number;
  items: WallTickerItem[];
}

export interface WallInsight {
  panelKey: string;
  label: string;
  text: string;
  watch: string | null;
  aiWritten: boolean;
  fromCache: boolean;
  valueKind: ValueKind;
  generatedAtUtc: string;
}

export interface WallWorkcenterTile {
  workcenterCode: string;
  workcenterName: string;
  shopCode: string | null;
  status: "running" | "overloaded" | "down" | "idle" | string;
  statusLabel: string;
  isBottleneck: boolean;
  activeOperatorCount: number;
  operatorAggregateLabel: string;
  readyOperationCount: number;
  queuedOperationCount: number;
  utilisationPercent: number | null;
  remainingHours: number;
  activeDowntimeReason: string | null;
  valueKind: ValueKind;
}

export interface WallFloorCommandPanel {
  runningCount: number;
  overloadedCount: number;
  downCount: number;
  idleCount: number;
  totalActiveOperators: number;
  bottleneckWorkcenterCode: string | null;
  bottleneckUtilisationPercent: number | null;
  workcenters: WallWorkcenterTile[];
}

export interface WallSqdcTileSummary {
  letter: string;
  initial: string;
  title: string;
  formattedValue: string;
  formattedTarget: string;
  formattedDelta: string;
  targetState: string;
  statusLabel: string;
  secondarySummary: string;
  sparklineValues: (number | null)[];
  valueKind: ValueKind;
}

export interface WallSqdcPanel {
  periodLabel: string;
  daysSinceLastRecordable: number | null;
  tiles: WallSqdcTileSummary[];
  recentSafetyDays: SqdcSafetyDay[];
}

export interface WallLateHotJobRow {
  workOrderKey: string;
  displayWorkOrder: string;
  partNumber: string;
  shopCode: string | null;
  priority: string;
  dueDate: string | null;
  forecastFinishDate: string | null;
  varianceWorkdays: number | null;
  healthTier: string;
  wipStage: string;
  valueKind: ValueKind;
}

export interface WallLateHotJobsPanel {
  activeHotCount: number;
  hotPriorityCap: number;
  lateHotCount: number;
  criticalTopLevelCount: number;
  atRiskTopLevelCount: number;
  onHoldCount: number;
  jobs: WallLateHotJobRow[];
}

export interface WallOspDueRow {
  key: string;
  workOrderKey: string;
  operationSequence: number | null;
  partNumber: string;
  processDescription: string;
  vendorCode: string;
  purchaseOrder: string | null;
  dueDate: string | null;
  daysDelta: number;
  statusLabel: string;
  tone: string;
  valueKind: ValueKind;
}

export interface WallOspDuePanel {
  openOspCount: number;
  overdueAtVendorCount: number;
  dueTodayOrTomorrowCount: number;
  averageTurnaroundWorkdays: number;
  items: WallOspDueRow[];
}

export interface WallProgramControlPointSummary {
  code: string;
  name: string;
  leadTimeOffsetWorkdays: number;
  lineOfBalanceQuantity: number;
  actualCompletedQuantity: number;
  shortfallUnits: number;
  status: string;
}

export interface WallProgramLobRow {
  programCode: string;
  programName: string;
  division: string;
  decodingSource: string;
  plannedAtStatusDate: number;
  actualAtStatusDate: number;
  deliveryGap: number;
  openWorkOrderCount: number;
  lateWorkOrderCount: number;
  overallStatus: string;
  overallStatusLabel: string;
  controlPoints: WallProgramControlPointSummary[];
  valueKind: ValueKind;
}

export interface WallProgramLobPanel {
  statusDate: string;
  totalPrograms: number;
  programsBehindCount: number;
  programs: WallProgramLobRow[];
}

export interface WallComingGoingCarrier {
  carrier: string;
  count: number;
  pickup: string | null;
  valueKind?: ValueKind;
}

export interface WallComingGoingPanel {
  goingOutTodayCount: number;
  goingByCarrier: WallComingGoingCarrier[];
  comingInTodayCount: number;
  comingOspReturnsCount: number;
  comingPoReceiptsCount: number;
  exceptionsCount: number;
  lateCount: number;
  weekGoingCount: number;
  weekComingCount: number;
  valueKind?: ValueKind;
}

export interface WallBoard {
  displayKey: string;
  title: string;
  boardKind: string;
  shopCode: string | null;
  scopeLabel: string;
  refreshSeconds: number;
  dwellSeconds: number;
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean;
  plantDate: string;
  configuredPanels: string[];
  activePanels: string[];
  availableDisplays: WallDisplaySummaryItem[];
  ticker: WallTicker;
  panelInsights: Record<string, WallInsight>;
  floorCommand: WallFloorCommandPanel | null;
  sqdc: WallSqdcPanel | null;
  lateHotJobs: WallLateHotJobsPanel | null;
  ospDue: WallOspDuePanel | null;
  programLob: WallProgramLobPanel | null;
  comingGoing?: WallComingGoingPanel | null;
}

export interface WallViperRefreshResult {
  displayKey: string;
  panelsRefreshed: number;
  refreshedAtUtc: string;
  panelInsights: Record<string, WallInsight>;
}

// ── Program LOB Types (O-R20, D-O11, D-O45, D-O86, Q-O6) ─────────────────────────────────────────

export interface ProgramObjectivePoint {
  date: string;
  label: string;
  plannedCumulative: number;
  actualCumulative: number | null;
  isStatusDate: boolean;
  isFuture: boolean;
  plannedValueKind: ValueKind;
  actualValueKind: ValueKind;
}

export interface ProgramControlPointBar {
  code: string;
  name: string;
  leadTimeOffsetWorkdays: number;
  targetDeliveryDate: string;
  lineOfBalanceQuantity: number;
  actualCompletedQuantity: number;
  gapQuantity: number;
  shortfallUnits: number;
  status: "OnTrack" | "Watch" | "Behind" | string;
  statusLabel: string;
  valueKind: ValueKind;
}

export interface ProgramContributingJob {
  workOrderKey: string;
  displayWorkOrder: string;
  partNumber: string;
  description: string;
  keyLevel: string;
  shopCode: string | null;
  quantityOrdered: number;
  quantityCompleted: number;
  workingDueDate: string | null;
  forecastFinishDate: string | null;
  varianceWorkdays: number | null;
  healthTier: string;
  wipStage: string;
  completedOperations: number;
  totalOperations: number;
  valueKind: ValueKind;
}

export interface ProgramContributingOrderLine {
  lineKey: string;
  orderNumber: string;
  recordNumber: string;
  customerCode: string;
  partNumber: string;
  quantityOrdered: number;
  quantityShipped: number;
  promiseDate: string | null;
  shipDate: string | null;
  statusLabel: string;
  valueKind: ValueKind;
}

export interface ProgramLobProgram {
  programCode: string;
  programName: string;
  division: string;
  decodingSource: string;
  decodingNote: string;
  statusDate: string;
  horizonFrom: string;
  horizonTo: string;
  plannedAtStatusDate: number;
  actualAtStatusDate: number;
  deliveryGapAtStatusDate: number;
  totalHorizonPlannedQuantity: number;
  totalShippedQuantity: number;
  datedDemandLineCount: number;
  undatedDemandLineCount: number;
  undatedDemandQuantity: number;
  openWorkOrderCount: number;
  lateWorkOrderCount: number;
  overallStatus: "OnTrack" | "Watch" | "Behind" | string;
  overallStatusLabel: string;
  insightTopic: string;
  insightSubjectKey: string;
  valueKind: ValueKind;
  objectiveCurve: ProgramObjectivePoint[];
  controlPoints: ProgramControlPointBar[];
  contributingJobs: ProgramContributingJob[];
  demandLines: ProgramContributingOrderLine[];
}

export interface ProgramLobOverview {
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean;
  dataSource: string;
  dataFloorDate: string;
  statusDate: string;
  selectedDivision: string;
  selectedProgramCode: string;
  hasPlMasterDecoding: boolean;
  decodingSummaryNote: string;
  availableDivisions: string[];
  programs: ProgramLobProgram[];
  activeProgram: ProgramLobProgram | null;
}

export interface GlobalSearchResultItem {
  id: string;
  category: string;
  title: string;
  subtitle: string;
  badge: string;
  linkPath: string;
  valueKind: ValueKind;
}

export interface GlobalSearchResponse {
  query: string;
  totalCount: number;
  generatedAtUtc: string;
  results: GlobalSearchResultItem[];
}

export interface CompletedOperationRow {
  operationKey: string;
  workOrderKey: string;
  displayWorkOrder: string;
  sequence: number;
  partNumber: string;
  description: string | null;
  workcenterCode: string | null;
  shopCode: string | null;
  completedOn: string;
  hoursEstimated: number;
  hoursActual: number;
  efficiencyPercent: number | null;
  valueKind: ValueKind;
}

export interface CompletedOperationsResponse {
  generatedAtUtc: string;
  fromDate: string;
  toDate: string;
  shopFilter: string | null;
  totalCompletedOperations: number;
  totalEstimatedHours: number;
  totalActualHours: number;
  aggregateEfficiencyPercent: number | null;
  availableShops: string[];
  operations: CompletedOperationRow[];
}

export const PARITY_QK = {
  sqdcBoard: (scope: string, period: string) => ["parity", "sqdc", scope, period] as const,
  sqdcDrill: (letter: string, scope: string, period: string, day: string) =>
    ["parity", "sqdc-drill", letter, scope, period, day] as const,
  sqdcTargets: ["parity", "sqdc-targets"] as const,
  wallBoard: (displayKey: string) => ["parity", "wall-board", displayKey] as const,
  programLob: (division: string, program: string) => ["parity", "program-lob", division, program] as const,
  globalSearch: (q: string) => ["parity", "search", q] as const,
  completedOps: (shop: string, days: number) => ["parity", "completed-ops", shop, days] as const
};

export const parityApi = {
  getSqdcBoard: async (params?: { scope?: string; period?: string }): Promise<SqdcBoard> => {
    const search = new URLSearchParams();
    if (params?.scope) search.set("scope", params.scope);
    if (params?.period) search.set("period", params.period);
    const qs = search.toString();
    return (await apiClient.get<SqdcBoard>(`/sqdc${qs ? `?${qs}` : ""}`)).data;
  },

  getSqdcDrill: async (params: { letter: string; scope?: string; period?: string; day?: string }): Promise<SqdcDrill> => {
    const search = new URLSearchParams({ letter: params.letter });
    if (params.scope) search.set("scope", params.scope);
    if (params.period) search.set("period", params.period);
    if (params.day) search.set("day", params.day);
    return (await apiClient.get<SqdcDrill>(`/sqdc/drill?${search.toString()}`)).data;
  },

  getSqdcTargets: async (): Promise<SqdcTarget[]> =>
    (await apiClient.get<SqdcTarget[]>("/sqdc/targets")).data,

  updateSqdcTarget: async (letter: string, payload: UpdateSqdcTargetPayload): Promise<SqdcTarget> =>
    (await apiClient.put<SqdcTarget>(`/sqdc/targets/${encodeURIComponent(letter)}`, payload)).data,

  getWallBoard: async (displayKey: string): Promise<WallBoard> =>
    (await apiClient.get<WallBoard>(`/display/${encodeURIComponent(displayKey)}/board`)).data,

  refreshWallViperInsights: async (displayKey: string, reason?: string): Promise<WallViperRefreshResult> =>
    (
      await apiClient.post<WallViperRefreshResult>(
        `/admin/wall-displays/${encodeURIComponent(displayKey)}/viper/refresh`,
        { reason: reason ?? "Manual admin refresh" }
      )
    ).data,

  getProgramLob: async (params?: { division?: string; program?: string }): Promise<ProgramLobOverview> => {
    const search = new URLSearchParams();
    if (params?.division) search.set("division", params.division);
    if (params?.program) search.set("program", params.program);
    const qs = search.toString();
    return (await apiClient.get<ProgramLobOverview>(`/operations/program-lob${qs ? `?${qs}` : ""}`)).data;
  },

  search: async (q: string): Promise<GlobalSearchResponse> =>
    (await apiClient.get<GlobalSearchResponse>(`/operations/search?q=${encodeURIComponent(q)}`)).data,

  getCompletedOperations: async (params?: { shop?: string; days?: number }): Promise<CompletedOperationsResponse> => {
    const search = new URLSearchParams();
    if (params?.shop) search.set("shop", params.shop);
    if (params?.days) search.set("days", String(params.days));
    const qs = search.toString();
    return (await apiClient.get<CompletedOperationsResponse>(`/operations/completed-ops${qs ? `?${qs}` : ""}`)).data;
  }
};
